import type { ComponentPropsWithRef, FC } from 'react';
import { Children, createContext, useContext, useMemo } from 'react';
import type { ActionMode } from 'src/components/action-mode';
import { ActionModeProvider } from 'src/components/action-mode';
import type { Size } from 'src/components/buttons-and-actions/button';
import { cn } from 'src/utils/cn';

export type ActionGroupMode = 'left' | 'middle' | 'right' | 'single';

const ModeContext = createContext<ActionGroupMode>('single');

export function useActionGroupMode() {
  return useContext(ModeContext);
}

export const ActionGroupModeProvider = ModeContext.Provider;

/**
 * Use this to group `<Button/>`'s or `<Links/>`'s.
 */
export const ActionGroup: FC<
  ComponentPropsWithRef<'div'> & {
    mode?: 'button-primary' | 'button-secondary' | 'segmented-control';
    size?: Size;
  }
> = ({ children, mode = 'button-primary', size = 'md', ref, ...props }) => {
  const truthyChildren = Children.toArray(children).filter(Boolean);
  const count = Children.count(truthyChildren);

  const actionMode = useMemo<ActionMode>(() => ({ mode, size }), [mode, size]);

  if (count === 0) return null;

  return (
    <div
      {...props}
      className={cn(`inline-grid grid-flow-col items-stretch`, props.className)}
      ref={ref}
    >
      <ActionModeProvider value={actionMode}>
        {Children.map(truthyChildren, (child, index) => {
          if (count === 1) {
            return (
              <ModeContext.Provider value="single">
                {child}
              </ModeContext.Provider>
            );
          } else if (index === 0) {
            return (
              <ModeContext.Provider value="left">{child}</ModeContext.Provider>
            );
          } else if (index + 1 === count) {
            return (
              <ModeContext.Provider value="right">{child}</ModeContext.Provider>
            );
          } else {
            return (
              <ModeContext.Provider value="middle">
                {child}
              </ModeContext.Provider>
            );
          }
        })}
      </ActionModeProvider>
    </div>
  );
};
